import type { $Img } from '@nuxt/image';

import { createImage } from '@nuxt/image/runtime';
import { imageOptions } from '#build/image-options.mjs';
import { joinURL } from 'ufo';

import type { ResolvedCurrentCluster } from '~/utils/clusters';

import { EMPTY_CLUSTER } from '~/utils/clusters';

/**
 * Cluster-prefix-aware image URLs.
 *
 * One Nuxt process serves several `(host, pathPrefix)` cluster bases from a
 * single build, so the ipx base path can't be baked in: @nuxt/image derives it
 * from `app.baseURL` (kept as `/`), emitting bare `/_ipx/...` URLs that Front
 * Door routes to the apex deployment instead of this cluster. `useImage()`
 * memoizes its image instance on `nuxtApp._img` (per request on SSR, singleton
 * on the client), so pre-creating that instance here with the cluster prefix
 * joined onto the baseURL makes every consumer — src, srcset, sizes, preload
 * links, KUI-layer NuxtImg usages — inherit the prefix for free. Root clusters
 * (empty prefix) skip the override and keep the stock lazy creation.
 *
 * The `createImage` call below mirrors @nuxt/image's `useImage()`
 * (dist/runtime/composables.js) exactly, apart from the baseURL — keep it in
 * sync when upgrading the module.
 */
export default defineNuxtPlugin({
  name: 'cluster-image',
  setup(nuxtApp) {
    const { pathPrefix } = useState<ResolvedCurrentCluster>('cluster', () => EMPTY_CLUSTER).value;
    if (!pathPrefix) {
      return;
    }

    const config = useRuntimeConfig();
    (nuxtApp as typeof nuxtApp & { _img?: $Img })._img = createImage({
      ...imageOptions,
      event: nuxtApp.ssrContext?.event,
      nuxt: {
        baseURL: joinURL(pathPrefix, config.app.baseURL),
      },
      runtimeConfig: config,
    });
  },
});
